import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { videos, categories } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

function VideoCard({ video }: { video: VideoItem }) {
  const navigate = useNavigate();

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/article/${video.id}`);
  };

  return (
    <article
      className="group cursor-pointer rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300"
      onClick={handleClick}
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={video.image}
          alt={video.title}
          title={`${video.title} - 青柠影视高清在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2 py-1 text-xs rounded-md bg-black/60 text-white whitespace-nowrap">
            {video.rating}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-2 py-1 text-xs rounded-md bg-primary-500 text-background-50 whitespace-nowrap">
            {video.status}
          </span>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-75 group-hover:scale-100">
            <i className="ri-play-fill text-2xl text-background-50 ml-1"></i>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {video.description}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span>{video.year}</span>
            <span>·</span>
            <span>{video.region}</span>
          </div>
          <span className="text-xs text-foreground-400">{video.type}</span>
        </div>
      </div>
    </article>
  );
}

function CategoryCard({ category, onClick }: { category: typeof categories[0]; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 cursor-pointer group min-w-[100px]"
    >
      <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-200">
        <i className={`${category.icon} text-xl text-primary-500 group-hover:text-background-50 transition-colors duration-200`}></i>
      </div>
      <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
        {category.name}
      </span>
    </button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const featuredVideos = videos.slice(0, 4);
  const hotVideos = videos.filter((v) => v.rating >= 8.5).slice(0, 6);
  const latestVideos = [...videos].sort((a, b) => b.year - a.year).slice(0, 8);
  const filteredVideos = activeCategory === 'all'
    ? videos
    : videos.filter((v) => v.category === activeCategory);

  const handleCategoryClick = (slug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/category/${slug}`);
  };

  const handleArticleClick = (id: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/article/${id}`);
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20home%20theater%20setup%20with%20large%20screen%20displaying%20abstract%20colorful%20visuals%2C%20cozy%20living%20room%20atmosphere%2C%20warm%20ambient%20lighting%2C%20cinematic%20experience%2C%20professional%20interior%20photography%20with%20soft%20shadows%20and%20inviting%20composition&width=1920&height=800&seq=hero-bg-qingning&orientation=landscape"
            alt="青柠影视 - 免费高清在线观影体验"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 max-w-3xl" style={{ fontFamily: 'var(--font-heading)' }}>
              免费观看 · 高清在线 · 不缓冲追剧
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl">
              海量影视资源每日更新，无需付费即可享受高清画质与极速播放，青柠影视是你免费追剧看片的理想选择。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => handleCategoryClick('recommend')}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>
                立即观看
              </button>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/20 hover:bg-white/30 text-white rounded-full text-base font-medium backdrop-blur-sm transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
              {categories.map((cat) => (
                <CategoryCard
                  key={cat.slug}
                  category={cat}
                  onClick={() => handleCategoryClick(cat.slug)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Hero Cards */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                <i className="ri-fire-line text-primary-500 mr-2"></i>
                精选推荐
              </h2>
              <Link to="/category/recommend" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot / High Rated */}
        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                <i className="ri-star-fill text-accent-500 mr-2"></i>
                高分必看
              </h2>
              <Link to="/category/recommend" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {hotVideos.map((video) => (
                <article
                  key={video.id}
                  className="group cursor-pointer rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300"
                  onClick={() => handleArticleClick(video.id)}
                >
                  <div className="relative overflow-hidden aspect-[3/4]">
                    <img
                      src={video.image}
                      alt={video.title}
                      title={`${video.title} - 青柠影视高清在线观看`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-1.5 py-0.5 text-xs rounded bg-black/60 text-white">
                        {video.rating}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-play-fill text-lg text-background-50 ml-0.5"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">{video.year} · {video.region}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                <i className="ri-timer-flash-line text-primary-500 mr-2"></i>
                最新上架
              </h2>
              <div className="flex items-center gap-2">
                {[
                  { slug: 'all', label: '全部' },
                  { slug: 'tv', label: '电视剧' },
                  { slug: 'movie', label: '电影' },
                  { slug: 'anime', label: '动漫' },
                ].map((tab) => (
                  <button
                    key={tab.slug}
                    onClick={() => setActiveCategory(tab.slug)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                      activeCategory === tab.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {filteredVideos.slice(0, 8).map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full bg-primary-500 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: '5000万+', label: '月活跃用户' },
                { value: '10万+', label: '影视资源' },
                { value: '99.9%', label: '播放成功率' },
                { value: '4.8分', label: '用户满意度' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <p className="text-sm text-background-50/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              马上开始你的追剧之旅
            </h2>
            <p className="text-base text-foreground-600 mb-8 max-w-xl mx-auto">
              无需注册，无需付费，打开青柠影视即可畅享海量高清影视内容。每日更新，精彩不断。
            </p>
            <button
              onClick={() => handleCategoryClick('recommend')}
              className="inline-flex items-center px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-rocket-line mr-2"></i>
              免费开始观看
            </button>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '青柠影视',
            url: 'https://www.qingning.tv',
            description: '青柠影视免费观看高清电视剧、热播剧和最新电影，在线不缓冲流畅追剧。海量影视资源每日更新，无需付费即可享受高清画质与极速播放。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.qingning.tv/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
    </>
  );
}